/**
 * Where you are, in the URL.
 *
 * The app has always kept its location in React state, which works until somebody presses Back
 * — and everybody presses Back. On a phone it is a system gesture, so "back" meaning "leave the
 * app entirely" is worse than a dead button.
 *
 * This is a hash route, deliberately, not the History API with real paths:
 *
 *   - The standalone build runs from `file://` on draft night. `pushState` with a path is a
 *     SecurityError there; a hash is not.
 *   - Firebase Hosting rewrites everything to the SPA already, but a hash needs no server
 *     agreement at all, so the offline file and the deployed app behave identically.
 *
 * `focus` — the "show me this pick" hand-off between screens — deliberately does NOT travel in
 * the URL. It is the reason for one navigation, not a location, and it expires with it.
 */

export interface Where {
  section: string;
  page: string;
  /**
   * A rule to open on arrival — the third segment, `#/league/rules/2015-II-2`.
   *
   * This one DOES travel in the URL, unlike `focus`. A citation is a destination: the whole point
   * of the twenty `§` marks scattered across FAAB, Trades, the Calculator and Draft Order is that
   * the link can be sent to somebody in Discord and land him on the rule.
   */
  ruleId?: string;
  /**
   * A tab WITHIN a page — the third segment when it is a slug rather than a rule id.
   * `#/league/rules/proposals`.
   *
   * League › Rules › Proposals is a sub-sub-menu, and until this existed it was React state with
   * no address: the only way to send somebody there was "open Rules, then click Proposals". The
   * page is the destination and the tab is part of it, so the tab belongs in the URL for the same
   * reason `ruleId` does — somebody is going to paste this into Discord.
   *
   * The DEFAULT tab is deliberately absent rather than written out. `#/league/rules` and
   * `#/league/rules/rules` would otherwise be two addresses for one screen, and the second is the
   * one that would end up in bookmarks.
   */
  view?: string;
}

/**
 * `#/keepers/slate` → `{ section: 'keepers', page: 'slate' }`, or null for anything else.
 *
 * Two extras beyond the plain two-segment form:
 *
 *   - A THIRD SEGMENT is a rule id — `#/league/rules/2015-II-2`. Rule ids are not slugs (they
 *     start with a digit and carry Roman numerals in caps), so they get their own shape test
 *     rather than being squeezed through `isSlug`.
 *   - A BARE RULE ID — `#2015-II-2` — routes to the rulebook. That is the form the design brief
 *     writes and the form somebody gets from copying an id out of a document, and without this it
 *     parses to null and silently does nothing.
 */
export function parseHash(hash: string): Where | null {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  if (parts.length === 1 && isRuleId(parts[0])) {
    return { section: 'league', page: 'rules', ruleId: parts[0]! };
  }
  if (parts.length < 2) return null;
  const [section, page, third] = parts;
  if (!isSlug(section) || !isSlug(page)) return null;
  /*
   * The third segment is one of two things and the test decides which. A rule id is checked FIRST
   * because it is the more specific shape — ids start with a digit and carry Roman numerals in
   * caps, so nothing that is a rule id could also be read as a tab slug.
   */
  if (isRuleId(third)) return { section: section!, page: page!, ruleId: third! };
  /*
   * `#/league/rules/byyear` IS A LEGACY ADDRESS AND MUST KEEP RESOLVING. It was a third tab until
   * 26 August; `design_handoff_rules_ia` made it an ordering on the Rules pane instead, because it
   * was never a different corpus — the same 132 rules, sorted differently. It is in Discord links
   * and in people's bookmarks, so it stays a valid slug and League › Rules lands it on that pane
   * with the year ordering already chosen. Do not "tidy" it away.
   */
  if (third && isSlug(third)) return { section: section!, page: page!, view: third };
  return { section: section!, page: page! };
}

export const formatHash = (at: Where) => {
  const third = at.ruleId ?? at.view ?? '';
  return `#/${at.section}/${at.page}${third ? `/${third}` : ''}`;
};

/*
 * ALL FOUR, and the last two are not optional to compare. Two citations to different rules on the
 * same page are two different places; comparing only section and page would make the second `§`
 * a no-op because the router already believed it was there. The same is true of the tab: without
 * `view` here, pressing Back out of Proposals would leave the URL correct and the screen wrong.
 */
export const sameWhere = (a: Where, b: Where) =>
  a.section === b.section && a.page === b.page
  && (a.ruleId ?? '') === (b.ruleId ?? '')
  && (a.view ?? '') === (b.view ?? '');

/*
 * Ids in this app are lowercase words. Anything else in the hash is somebody else's — an anchor,
 * a password-manager artefact, a hand-typed URL — and is left alone rather than routed to.
 */
const isSlug = (s: string | undefined) => Boolean(s && /^[a-z][a-z0-9-]*$/.test(s));

/*
 * `2015-II-2` and `2022-trade-declarations` — the two shapes the rulebook uses. Deliberately not
 * "anything that is not a slug": a hash this does not recognise is left alone, which is what keeps
 * a password manager's `#u=1` artefact from being routed to.
 */
const isRuleId = (s: string | undefined) =>
  Boolean(s && /^\d{4}-(?:[IVX]+-\d+|[a-z][a-z0-9-]{3,})$/.test(s));
